///ToplineTable.jsx

import React, { useEffect, useState, useContext, useRef } from 'react';
import { Collapse, Table, Button, Input, message, Spin, Modal } from 'antd';
import { useSelector } from 'react-redux';
import api from '../functions/api';
import getUserId from '../functions/getUserId';
import { prefetchAccount } from '../functions/accountPortfolioCache';  // DAS-2692: background preload of broker portfolios
import AccountPortfolioModal from './AccountPortfolioModal';
import IncomeDetailsModal from './IncomeDetailsModal';  // DAS-2621: dividend payout drill-down
import CostsSection from './CostsSection';  // DAS-2777: costs per component, by type
import ScoreboardPanel from './ScoreboardPanel';       // DAS-2766 (placed here by DAS-2792)
import RebuyHistoryPanel from './RebuyHistoryPanel';   // DAS-2770 (placed here by DAS-2792)
import InfoTooltip from './InfoTooltip';  // DAS-2536: (i) explanation
import ClosedTradesPanel from './ClosedTradesPanel';  // DAS-2826: completed trades
import SleeveGapChart from './SleeveGapChart';  // DAS-3307: gap-to-benchmark history, line per account
import SleeveHoldingEffects from './SleeveHoldingEffects';  // DAS-3360: monthly note, which holdings moved the gap
import { passmarkChip } from '../functions/passmarkChip';  // DAS-3319: PASS/FAIL label, rule on hover


const { Panel } = Collapse;

const formatEuro = (value) => {
  if (value == null || value === '') return '€ 0,00';
  const num = typeof value === 'number' ? value : parseFloat(String(value).replace(/,/g, ''));
  if (isNaN(num)) return '€ 0,00';
  return `€ ${num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const ToplineTable = ({ updatedCategoryRecords, onOpenOtherInvestments }) => {
  const [showSaxo, setShowSaxo] = useState(false);
  const [showDeGiro, setShowDeGiro] = useState(false);
  const userId = getUserId();
  const demoMode = useSelector(state => state.demoMode.isDemoMode);

  // DAS-2903: time-weighted YTD (the home chart's headline number) shown under
  // the Overall return cell, so both measures are visible here too.
  const [twrPerformance, setTwrPerformance] = useState(null);
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    api.get('/stockchart_mui', {
      params: { code: 'portfolio_performance', userid: userId, period: 'YTD' }
    })
      .then(res => {
        if (cancelled) return;
        if (res.data?.status === 'success' && typeof res.data.performance === 'number') {
          setTwrPerformance(res.data.performance);
        } else {
          console.warn('DAS-2903: Unexpected response format:', res.data);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('DAS-2903: Failed to fetch TWR performance:', err?.message || err);
        }
      });
    return () => { cancelled = true; };
  }, [userId]);
  const [latestRefreshEffort, setLatestRefreshEffort] = useState('N/A');
  // DAS-2920: the Saxo reconnect no longer sits in the page flow; it opens in its own
  // dialog from the Saxo row, where the (disabled) Update button explains why you need it.
  const [reconnectOpen, setReconnectOpen] = useState(false);
  const [smsCode, setSmsCode] = useState('');  // Define smsCode and setSmsCode
  const [is2FAStep, setIs2FAStep] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // To track loading state
  const [isSmsLoading, setIsSmsLoading] = useState(false); // For SMS submission loading
  const [isUpdateLoading, setIsUpdateLoading] = useState({
    overall: false,
    saxo: false,
    degiro: false,
    degiro_rebalance: false,
    other: false
  });
  // DAS-1939: State for account portfolio modal
  const [accountModalVisible, setAccountModalVisible] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedAccountDisplayName, setSelectedAccountDisplayName] = useState(null);
  const [selectedAccountTopline, setSelectedAccountTopline] = useState(null);
  // DAS-2621: Income-payout drill-down modal (click an "Inc €" cell in Return by part)
  const [incomeModal, setIncomeModal] = useState({ open: false, part: null, partLabel: '', period: null, periodLabel: '' });
  const openIncomeModal = (part, partLabel, period, periodLabel) =>
    setIncomeModal({ open: true, part, partLabel, period, periodLabel });
  const closeIncomeModal = () => setIncomeModal(prev => ({ ...prev, open: false }));
  // DAS-1939: Handler for clicking account column headers
  const handleAccountHeaderClick = (account, displayName, toplineData) => {
    setSelectedAccount(account);
    setSelectedAccountDisplayName(displayName);
    setSelectedAccountTopline(toplineData);
    setAccountModalVisible(true);
  };
    const [isAnyUpdateLoading, setIsAnyUpdateLoading] = useState(false); // New state to track if any update is in progress
    const auth = useSelector(state => state.auth);
    const portfolio = useSelector(state => state.portfolio);
    const etf = useSelector(state => state.etf);
    const favorites = useSelector(state => state.favorites);
    const widget = useSelector(state => state.widget);
    useEffect(() => {
      console.log("All State:", {
        auth,
        demoMode,
        portfolio,
        etf,
        favorites,
        widget
      });
    
      const portfolioRecords = portfolio?.data?.portfolio || [];
      const manualRecords = portfolioRecords.filter(record => record.account === 'manual');
      // console.log("Manual Account Records:", manualRecords);
    }, []);

    const portfolioRecords = portfolio?.data?.portfolio || [];
    const manualRecords = portfolioRecords.filter(record => record.account === 'manual');
    const manualValueTotal = manualRecords.reduce((sum, item) => sum + parseFloat(item.Value_eur || 0), 0);
    const formattedManualTotal = formatEuro(manualValueTotal);




  const handleUpdateClick = (column) => {
    console.log('Clicked column:', column);
    setIsAnyUpdateLoading(true); // Disable all update buttons
    setIsUpdateLoading(prevState => ({ ...prevState, [column]: true })); // Show spinner for the clicked column
  
    if (column === 'overall') {
      api.post('/update_overall', { userId })
        .then(response => {
          const { results } = response.data;
          message.success(`Overall update completed: ${results.wando}, ${results.degiro}, ${results.saxo}`);
        })
        .catch(error => {
          message.error('Error updating overall portfolio');
          console.error('Error updating overall portfolio:', error);
        })
        .finally(() => {
          setIsUpdateLoading(prevState => ({ ...prevState, [column]: false }));
          setIsAnyUpdateLoading(false); // Re-enable all buttons after the process
        });
    } else if (column === 'degiro') {
      api.post('/update_dg', { userId })
        .then(response => {
          message.success("DeGiro portfolio updated successfully");
        })
        .catch(error => {
          message.error("Error updating DeGiro portfolio");
          console.error('Error updating DeGiro portfolio:', error);
        })
        .finally(() => {
          setIsUpdateLoading(prevState => ({ ...prevState, [column]: false }));
          setIsAnyUpdateLoading(false); // Re-enable all buttons after the process
        });
    } else if (column === 'degiro_rebalance') {
      api.post('/update_dg', { userId })
        .then(response => {
          message.success("DeGiro Rebalance portfolio updated successfully");
        })
        .catch(error => {
          message.error("Error updating DeGiro Rebalance portfolio");
          console.error('Error updating DeGiro Rebalance portfolio:', error);
        })
        .finally(() => {
          setIsUpdateLoading(prevState => ({ ...prevState, [column]: false }));
          setIsAnyUpdateLoading(false); // Re-enable all buttons after the process
        });
    } else if (column === 'saxo') {
      api.post('/update_s', { userId })
        .then(response => {
          message.success("Saxo portfolio updated successfully");
        })
        .catch(error => {
          message.error("Error updating Saxo portfolio");
          console.error('Error updating Saxo portfolio:', error);
        })
        .finally(() => {
          setIsUpdateLoading(prevState => ({ ...prevState, [column]: false }));
          setIsAnyUpdateLoading(false); // Re-enable all buttons after the process
        });
    } else if (column === 'other') {
      api.post('/update_o', { userId })
        .then(response => {
          message.success("W&O portfolio updated successfully");
        })
        .catch(error => {
          message.error("Error updating W&O portfolio");
          console.error('Error updating W&O portfolio:', error);
        })
        .finally(() => {
          setIsUpdateLoading(prevState => ({ ...prevState, [column]: false }));
          setIsAnyUpdateLoading(false); // Re-enable all buttons after the process
        });
    } else {
      message.info(`Update button clicked for ${column}`);
      setIsUpdateLoading(prevState => ({ ...prevState, [column]: false }));
      setIsAnyUpdateLoading(false); // Ensure all buttons are re-enabled
    }
  };

  const handleSmsSubmit = () => {
    setIsSmsLoading(true); // Show spinner while processing SMS code submission
    api.post('/renew_S_token_2', { userId, sms_code: smsCode })
      .then(response => {
        message.success("Saxo login completed successfully");
        setIsSmsLoading(false); // Stop the spinner
        setIs2FAStep(false); // Hide the SMS input
  
        // Re-fetch the token status to update the latestRefreshEffort
        api.post('/token_status', { userId })
          .then(response => {
            if (response.data && response.data.result_latest_refresh_effort) {
              setLatestRefreshEffort(response.data.result_latest_refresh_effort);
            } else {
              setLatestRefreshEffort('N/A');
            }
          })
          .catch(error => {
            message.error('Error fetching updated token status');
            console.error('Error fetching token status after SMS submit:', error);
          });
  
      })
      .catch(error => {
        message.error('Error completing token refresh');
        setIsSmsLoading(false); // Stop the spinner if error
        setIs2FAStep(true); // Keep showing the SMS input for retry
        console.error('Error completing token refresh:', error);
      });
  };


  // Handle the refresh token request
  const handleRefreshToken = () => {
    setIsLoading(true); // Show spinner for refresh token process
    api.post('/renew_S_token_1', { userId })
      .then(response => {
        if (response.data.message === "Saxo login initiated, waiting for 2FA code") {
          message.success("Saxo login initiated, waiting for 2FA code");
          setIs2FAStep(true); // Show the SMS input and button
        }
        setIsLoading(false); // Stop the spinner
      })
      .catch(error => {
        // DAS-2881: surface the backend's reason (e.g. Saxo rejected the stored password)
        const serverMsg = error?.response?.data?.error;
        message.error(serverMsg || 'Error initiating token refresh', serverMsg ? 10 : 3);
        setIsLoading(false); // Stop the spinner in case of error
        console.error('DAS-2881 token refresh error:', error);
      });
  };

  
  
 
  useEffect(() => {
    const userId = getUserId();
  
    // Call the new API to get the result_latest_refresh_effort
    api.post('/token_status', { userId })
      .then(response => {
        if (response.data && response.data.result_latest_refresh_effort) {
          setLatestRefreshEffort(response.data.result_latest_refresh_effort);
  
          // Log the value directly from the response
          // console.log("Latest effort ", response.data.result_latest_refresh_effort);
        } else {
          setLatestRefreshEffort('N/A');
        }
      })
      .catch(error => {
        console.error('Error fetching token status:', error);
        setLatestRefreshEffort('N/A');
      });
  
    // Existing API call to check for authorization
    api.post('/Broker_checkifauthorisationavailable', { userId })
      .then(response => {
        setShowSaxo(response.data.saxo);
        setShowDeGiro(response.data.degiro);
      })
      .catch(error => {
        console.error('Error checking authorization:', error);
      });
  }, []);
  

  const overallTopline = useSelector(state => state.portfolio && state.portfolio.data ? state.portfolio.data.portfolio_topline : null);
  const saxoTopline = useSelector(state => state.portfolio && state.portfolio.data ? state.portfolio.data.saxo_topline : null);
  const dgTopline = useSelector(state => state.portfolio && state.portfolio.data ? state.portfolio.data.dg_portfolio_topline : null);
  const other =useSelector(state => state.portfolio && state.portfolio.data ? state.portfolio.data.Topline_Other: null);
  // DAS-2558: per-part matrix — 1D/1W/1M price (TWRR) + income per period from backend;
  // YTD price comes from the exact broker toplines below.
  const partReturns = useSelector(state => state.portfolio && state.portfolio.data ? state.portfolio.data.portfolio_part_returns : null);
  // DAS-2740: portfolio-wide YTD income split by category (equity dividend / bond coupon / interest / reinvested-memo)
  const incomeCategoryTotals = useSelector(state => (state.portfolio && state.portfolio.data && state.portfolio.data.income_category_totals) || null);
  // DAS-2726 (slice 4): per-sleeve risk-matched benchmark + risk for the fair-comparison panel
  const sleeveBenchmark = useSelector(state => (state.portfolio && state.portfolio.data && state.portfolio.data.sleeve_benchmark) || []);
  // DAS-3208: per-account PASS/FAIL against the owner's own pass mark (strategy section 15)
  const sleevePassmark = useSelector(state => (state.portfolio && state.portfolio.data && state.portfolio.data.sleeve_passmark) || []);
  const sleeveRisk = useSelector(state => (state.portfolio && state.portfolio.data && state.portfolio.data.sleeve_risk) || []);
  // DAS-2824: the owner's own crash behaviour (Panic_Review_Log) — history shown under the Fair comparison, not an alert
  const panicReview = useSelector(state => (state.portfolio && state.portfolio.data && state.portfolio.data.panic_review) || []);
  // DAS-2752: per-part euro reconciliation (start value → current value − deposits = € earned).
  // Backend only emits parts whose inputs reconcile sanely (reliability gate).
  const partRecon = useSelector(state => (state.portfolio && state.portfolio.data && state.portfolio.data.part_reconciliation) || {});
  // DAS-2751: every part's YTD measured by ONE method (our daily/monthly series),
  // carried side-by-side with the broker figures; hover flags divergence.
  const partUniform = useSelector(state => (state.portfolio && state.portfolio.data && state.portfolio.data.part_returns_uniform) || {});

  // Determine if the 'Other' section should be shown
  const showOther = other && other.length > 0;
  const otherName = showOther ? other[0].name : 'Other';

  const overallToplineData = (overallTopline && overallTopline[0]) || {};
  // console.log("overalltoplinedata:",overallToplineData);
  const saxoToplineData = (saxoTopline && saxoTopline[0]) || {};
  // console.log("saxotoplinedata:",saxoToplineData);
  
  // Parse dgTopline array to separate by account field
  // Regular DeGiro: account='' or NULL
  // DeGiro Rebalance: account='rebalance'
  const dgToplineData = (dgTopline && dgTopline.length > 0) 
    ? dgTopline.find(dg => !dg.account || dg.account === '') || {}
    : {};
  const dgRebalanceToplineData = (dgTopline && dgTopline.length > 0)
    ? dgTopline.find(dg => dg.account === 'rebalance') || null
    : null;
  const showDeGiroRebalance = dgRebalanceToplineData !== null;
  
  // console.log("dgtoplinedata:",dgToplineData);
  // console.log("dgRebalanceToplineData:",dgRebalanceToplineData);
  const otherData = (other && other[0]) || {};

  // DAS-2692: when the Portfolio modal is open, pre-load each visible broker's
  // positions table in the BACKGROUND (into accountPortfolioCache) so clicking a
  // broker opens instantly instead of showing "Loading portfolio data…". A small
  // badge on each broker header shows loading → ✓ ready, with a message when ready.
  const [preloadStatus, setPreloadStatus] = useState({});  // account -> 'loading'|'ready'|'error'
  const preloadStartedRef = useRef({});                     // account -> true once kicked off

  useEffect(() => {
    if (!userId) return;
    const targets = [];
    if (showSaxo) targets.push(['saxo', 'Saxo']);
    if (showDeGiro) targets.push(['degiro', 'DeGiro']);
    if (showDeGiroRebalance) targets.push(['degiro_rebalance', 'DeGiro Rebalance']);
    if (showOther) targets.push(['weno', otherName]);
    targets.forEach(([acct, label]) => {
      if (preloadStartedRef.current[acct]) return;         // once per broker per mount
      preloadStartedRef.current[acct] = true;
      setPreloadStatus(prev => ({ ...prev, [acct]: 'loading' }));
      prefetchAccount(userId, acct)
        .then(() => {
          setPreloadStatus(prev => ({ ...prev, [acct]: 'ready' }));
          message.success(`${label} portfolio loaded`);
        })
        .catch(() => setPreloadStatus(prev => ({ ...prev, [acct]: 'error' })));
    });
  }, [userId, showSaxo, showDeGiro, showDeGiroRebalance, showOther, otherName]);

  // Small status badge shown next to a broker's clickable header.
  const preloadBadge = (acct) => {
    const s = preloadStatus[acct];
    if (s === 'loading') return <Spin size="small" style={{ marginLeft: 6 }} />;
    if (s === 'ready') return <span style={{ marginLeft: 6, color: '#52c41a' }} title="Loaded — opens instantly">✓</span>;
    if (s === 'error') return <span style={{ marginLeft: 6, color: '#ff4d4f' }} title="Preload failed — will load on click">!</span>;
    return null;
  };

  const renderIndicatorLabel = (text) => {
    if (text === 'Tradeable Asset Value') {
        return (
            <div>
                {text}
                <div style={{ fontSize: 'smaller', marginTop: '4px', fontStyle: 'italic' }}>cash</div>
            </div>
        );
    } else if (text === 'Overall return') {
        return (
            <div>
                {text}
                <div style={{ fontSize: 'smaller', marginTop: '4px', fontStyle: 'italic' }}>performance</div>
            </div>
        );
    }
    return text;
};

const renderCashAvailable = (text, record, column) => {
  let secondaryValue = '';
  let tertiaryValue = '';
  let secondaryStyle = { fontSize: 'smaller', marginTop: '4px', fontStyle: 'italic' };

  // Check if demoMode is true
  const blurClass = demoMode ? 'blur-text' : '';

  switch (column) {
    case 'overall':
      if (record.indicator === 'Tradeable Asset Value') secondaryValue = record.overallCash;
      if (record.indicator === 'Overall return') {
        secondaryValue = record.overallPerformance;
        // DAS-2903: the home chart's time-weighted number, side by side
        if (twrPerformance != null) tertiaryValue = `time-weighted: ${twrPerformance.toFixed(2)}%`;
      }
      break;
    case 'saxo':
      if (record.indicator === 'Tradeable Asset Value') secondaryValue = record.saxoCash;
      if (record.indicator === 'Overall return') secondaryValue = record.saxoPerformance;
      break;
    case 'degiro':
      if (record.indicator === 'Tradeable Asset Value') secondaryValue = record.degiroCash;
      if (record.indicator === 'Overall return') secondaryValue = record.degiroPerformance;
      break;
    case 'degiro_rebalance':
      if (record.indicator === 'Tradeable Asset Value') secondaryValue = record.degiroRebalanceCash;
      if (record.indicator === 'Overall return') secondaryValue = record.degiroRebalancePerformance;
      break;
    case 'other':
      if (record.indicator === 'Tradeable Asset Value') secondaryValue = record.otherCash;
      if (record.indicator === 'Overall return') secondaryValue = record.otherPerformance;
      break;
  }

  // For the "Latest update" row, return only the primary value (i.e., "X hours ago") and no secondary value
  if (record.indicator === 'Latest update') {
    return (
      <div>
        {text} {/* This will display "X hours ago" */}
        <Button 
          type="primary" 
          onClick={() => handleUpdateClick(column)} 
          style={{ marginTop: '8px' }}
          size="small"
          disabled={isAnyUpdateLoading || (column === 'saxo' && latestRefreshEffort !== '200' && latestRefreshEffort !== '201')} // Disable all buttons if any update is in progress
        >
          {isUpdateLoading[column] ? <Spin /> : 'Update'}
        </Button>
      </div>
    );
  }

  return (
    <div>
      {/* Apply the blur class conditionally */}
      <span className={blurClass}>{text}</span>
      <div style={secondaryStyle} className={blurClass}>{secondaryValue}</div>
      {tertiaryValue && (
        <div style={secondaryStyle} className={blurClass} data-testid="status-twr-secondary">
          {tertiaryValue}
        </div>
      )}
    </div>
  );
};





function calculateHoursAgo(epoch) {
  const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
  const timeDifference = currentTime - epoch; // Difference in seconds
  const hoursAgo = Math.floor(timeDifference / 3600); // Convert to hours
  return `${hoursAgo} hours ago`;
}


  const tradeableValue = parseFloat(overallToplineData.portfolio_total_value) || 0;
  const totalAssetValue = tradeableValue + manualValueTotal;

  const dataSource = [
    ...(manualRecords.length > 0 ? [{
      key: '0',
      indicator: 'Total Asset Value',
      overall: formatEuro(totalAssetValue),
      saxo: '',
      degiro: '',
      degiro_rebalance: '',
      other: '',
    }] : []),
    {
      key: '1',
      indicator: 'Tradeable Asset Value',
      overall: formatEuro(overallToplineData.portfolio_total_value),
      overallCash: formatEuro(overallToplineData.cash_eur),
      saxo: formatEuro(saxoToplineData.Saxo_portfolio_total_value),
      saxoCash: formatEuro(saxoToplineData.Saxo_cash_eur),
      degiro: formatEuro(dgToplineData.portfolio_total_value),
      degiroCash: formatEuro(dgToplineData.cash_eur_value),
      degiro_rebalance: formatEuro(dgRebalanceToplineData?.portfolio_total_value),
      degiroRebalanceCash: formatEuro(dgRebalanceToplineData?.cash_eur_value),
      other: formatEuro(otherData.value),
      savings: formattedManualTotal,
    },
    {
      key: '2',
      indicator: 'Overall return',
      overall: formatEuro(overallToplineData.portfolio_result),
      overallPerformance: overallToplineData.portfolio_performance ? `${overallToplineData.portfolio_performance}%` : 'N/A',
      saxo: formatEuro(saxoToplineData.Saxo_portfolio_result),
      saxoPerformance: saxoToplineData.Saxo_portfolio_performance ? `${saxoToplineData.Saxo_portfolio_performance}%` : 'N/A',
      degiro: formatEuro(dgToplineData.portfolio_result),
      degiroPerformance: dgToplineData.portfolio_performance ? `${dgToplineData.portfolio_performance}%` : 'N/A',
      degiro_rebalance: formatEuro(dgRebalanceToplineData?.portfolio_result),
      degiroRebalancePerformance: dgRebalanceToplineData?.portfolio_performance ? `${dgRebalanceToplineData.portfolio_performance}%` : 'N/A',
      other: formatEuro(otherData.result_value),
      otherPerformance: otherData.performance ? ` ${otherData.performance}%` : 'N/A',
    },
    {
      key: '3',
      indicator: 'Latest update',
      overall: overallToplineData.epoch ? calculateHoursAgo(overallToplineData.epoch) : 'N/A',
      saxo: saxoToplineData.epoch ? calculateHoursAgo(saxoToplineData.epoch) : 'N/A',
      degiro: dgToplineData.epoch ? calculateHoursAgo(dgToplineData.epoch) : 'N/A',
      degiro_rebalance: dgRebalanceToplineData?.epoch ? calculateHoursAgo(dgRebalanceToplineData.epoch) : 'N/A',
      other: otherData.epoch ? calculateHoursAgo(otherData.epoch) : 'N/A',
    }
  ];
  
  const baseColumns = [
   {
        title: '',
        dataIndex: 'indicator',
        key: 'indicator',
        render: renderIndicatorLabel // Use this custom render function for the indicator column
    },
    {
        title: 'Overall',
        dataIndex: 'overall',
        key: 'overall',
        render: (text, record) => renderCashAvailable(text, record, 'overall')
    }
  ];
  
  if (showSaxo) {
    baseColumns.push({
      // DAS-1939: Clickable header to show Saxo portfolio details
      title: (
        <span 
          onClick={() => handleAccountHeaderClick('saxo', 'Saxo', {
            totalValue: saxoToplineData.Saxo_portfolio_total_value,
            cash: saxoToplineData.Saxo_cash_eur,
            result: saxoToplineData.Saxo_portfolio_result,
            performance: saxoToplineData.Saxo_portfolio_performance
          })} 
          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
          title="Click to view Saxo portfolio details"
        >
          Saxo{preloadBadge('saxo')}
        </span>
      ),
      dataIndex: 'saxo',
      render: (text, record) => renderCashAvailable(text, record, 'saxo')
    });
  }
  
  if (showDeGiro) {
    baseColumns.push({
      // DAS-1939: Clickable header to show DeGiro portfolio details
      title: (
        <span 
          data-testid="topline-account-degiro"
          onClick={() => handleAccountHeaderClick('degiro', 'DeGiro', {
            totalValue: dgToplineData.portfolio_total_value,
            cash: dgToplineData.cash_eur_value,
            result: dgToplineData.portfolio_result,
            performance: dgToplineData.portfolio_performance
          })} 
          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
          title="Click to view DeGiro portfolio details"
        >
          DeGiro{preloadBadge('degiro')}
        </span>
      ),
      dataIndex: 'degiro',
      key: 'degiro',
      render: (text, record) => renderCashAvailable(text, record, 'degiro')
    });
  }
  
  if (showDeGiroRebalance) {
    baseColumns.push({
      // DAS-1939: Clickable header to show DeGiro Rebalance portfolio details
      title: (
        <span 
          onClick={() => handleAccountHeaderClick('degiro_rebalance', 'DeGiro Rebalance', {
            totalValue: dgRebalanceToplineData?.portfolio_total_value,
            cash: dgRebalanceToplineData?.cash_eur_value,
            result: dgRebalanceToplineData?.portfolio_result,
            performance: dgRebalanceToplineData?.portfolio_performance
          })} 
          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
          title="Click to view DeGiro Rebalance portfolio details"
        >
          DeGiro Rebalance{preloadBadge('degiro_rebalance')}
        </span>
      ),
      dataIndex: 'degiro_rebalance',
      key: 'degiro_rebalance',
      render: (text, record) => renderCashAvailable(text, record, 'degiro_rebalance')
    });
  }
  
  if (showOther) {
    baseColumns.push({
      // DAS-1939: Clickable header to show W&O/Other portfolio details
      title: (
        <span 
          onClick={() => handleAccountHeaderClick('weno', otherName, {
            totalValue: otherData.value,
            cash: otherData.cashinenout,
            result: otherData.result_value,
            performance: otherData.performance
          })} 
          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
          title={`Click to view ${otherName} portfolio details`}
        >
          {otherName}{preloadBadge('weno')}
        </span>
      ),
      dataIndex: 'other',
      key: 'other',
      render: (text, record) => renderCashAvailable(text, record, 'other')
    });
  }

  if (manualRecords.length > 0) {
    baseColumns.push({
      // DAS-2179: Clickable header to show Other Assets details modal
      title: (
        <span
          onClick={() => onOpenOtherInvestments?.()}
          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
          title="Click to view Other Investments"
        >
          Other Assets
        </span>
      ),
      dataIndex: 'savings',
      key: 'savings',
      render: (text) => (
        <span className={demoMode ? 'blur-text' : ''}>{text}</span>
      )
    });
  }
  
  

  
  const portfolioData = overallTopline ? overallTopline[0] : {};

 // Conditional styling based on demo mode
 const headerStyle = demoMode ? "blur-text" : "";
 const resultText = portfolioData.portfolio_result ? formatEuro(portfolioData.portfolio_result) : 'N/A';
 const performanceText = portfolioData.portfolio_performance ? `${portfolioData.portfolio_performance}%` : 'N/A';

 // DAS-2536: colour-coded signed percentage for the period-return strip.
 const fmtPctColored = (v) => {
   if (v == null) return <span style={{ color: '#888' }}>—</span>;
   const c = v > 0 ? '#2e7d32' : v < 0 ? '#c62828' : '#555';
   return <span style={{ color: c, fontWeight: 600 }}>{v > 0 ? '+' : ''}{v.toFixed(2)}%</span>;
 };

 // ---------------------------------------------------------------------------
 // DAS-2920 (Shape A): the screen opens with the answer, not with five tables.
 // Everything below is derived from data already fetched for the blocks that are
 // now folded away — no new endpoint, no new state.
 // ---------------------------------------------------------------------------
 const numOf = (x) => { const v = parseFloat(x); return isNaN(v) ? null : v; };
 const euro0 = (v) => (v == null ? '—' : `€ ${Math.round(v).toLocaleString('nl-NL')}`);

 // Each sleeve's own risk-matched benchmark (the "Fair comparison" numbers).
 const benchYtd = {};
 (sleeveBenchmark || []).forEach(b => { benchYtd[b.account] = numOf(b.bench_ytd); });

 // DAS-3208: per-account pass mark, keyed the same way as sleeve_benchmark's account field.
 const passmarkByAccount = {};
 (sleevePassmark || []).forEach(p => { passmarkByAccount[p.account] = p; });

 const overallResultEur = numOf(overallToplineData.portfolio_result);
 const overallYtdPct = numOf(overallToplineData.portfolio_performance);
 const overallBenchPct = benchYtd.overall != null ? benchYtd.overall : null;
 const overallDiff = (overallYtdPct != null && overallBenchPct != null)
   ? overallYtdPct - overallBenchPct : null;

 // The Saxo connection is dead when its last re-auth attempt did not come back 200/201.
 const saxoNeedsReconnect = showSaxo && latestRefreshEffort !== '200' && latestRefreshEffort !== '201';

 // Which part pulled which way — ranked by distance from its OWN fair benchmark,
 // worst first, because that is the row worth looking at.
 const rankedParts = [
   { show: showSaxo, benchKey: 'saxo', updateKey: 'saxo', acctKey: 'saxo', label: 'Saxo',
     value: numOf(saxoToplineData.Saxo_portfolio_total_value),
     ytd: numOf(saxoToplineData.Saxo_portfolio_performance),
     epoch: saxoToplineData.epoch,
     topline: { totalValue: saxoToplineData.Saxo_portfolio_total_value, cash: saxoToplineData.Saxo_cash_eur,
                result: saxoToplineData.Saxo_portfolio_result, performance: saxoToplineData.Saxo_portfolio_performance } },
   { show: showDeGiro, benchKey: 'degiro', updateKey: 'degiro', acctKey: 'degiro', label: 'DeGiro',
     value: numOf(dgToplineData.portfolio_total_value),
     ytd: numOf(dgToplineData.portfolio_performance),
     epoch: dgToplineData.epoch,
     topline: { totalValue: dgToplineData.portfolio_total_value, cash: dgToplineData.cash_eur_value,
                result: dgToplineData.portfolio_result, performance: dgToplineData.portfolio_performance } },
   { show: showDeGiroRebalance, benchKey: 'degiro_rebalance', updateKey: 'degiro_rebalance',
     acctKey: 'degiro_rebalance', label: 'DeGiro Rebalance',
     value: numOf(dgRebalanceToplineData && dgRebalanceToplineData.portfolio_total_value),
     ytd: numOf(dgRebalanceToplineData && dgRebalanceToplineData.portfolio_performance),
     epoch: dgRebalanceToplineData && dgRebalanceToplineData.epoch,
     topline: { totalValue: dgRebalanceToplineData && dgRebalanceToplineData.portfolio_total_value,
                cash: dgRebalanceToplineData && dgRebalanceToplineData.cash_eur_value,
                result: dgRebalanceToplineData && dgRebalanceToplineData.portfolio_result,
                performance: dgRebalanceToplineData && dgRebalanceToplineData.portfolio_performance } },
   { show: showOther, benchKey: 'WenO', updateKey: 'other', acctKey: 'weno', label: otherName,
     value: numOf(otherData.value),
     ytd: numOf(otherData.performance),
     epoch: otherData.epoch,
     topline: { totalValue: otherData.value, cash: otherData.cashinenout,
                result: otherData.result_value, performance: otherData.performance } },
 ]
   .filter(p => p.show)
   .map(p => ({ ...p, diff: (p.ytd != null && benchYtd[p.benchKey] != null) ? p.ytd - benchYtd[p.benchKey] : null }))
   .sort((a, b) => {
     if (a.diff == null && b.diff == null) return 0;
     if (a.diff == null) return 1;          // parts without a fair benchmark go last
     if (b.diff == null) return -1;
     return a.diff - b.diff;                 // worst first
   });

 // A short bar next to each part, scaled to how far it is from its benchmark
 // (capped at 20 points so one bad sleeve doesn't flatten the others).
 const gapBarWidth = (d) => (d == null ? 0 : Math.min(80, Math.round(Math.abs(d) / 20 * 80)));

 const kickerStyle = { fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#999' };
 const seclineStyle = { fontSize: 12, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase', color: '#666' };
 // DAS-3319: the pass mark has its own column, and no cell may spill into its neighbour
 // (the long pass-mark sentence used to run left over the value and YTD columns).
 const partRowStyle = {
   display: 'grid', gridTemplateColumns: 'minmax(130px,1.3fr) minmax(90px,0.8fr) minmax(70px,0.6fr) minmax(170px,1.3fr) minmax(70px,0.5fr) minmax(130px,0.9fr)',
   gap: 12, alignItems: 'center', padding: '9px 2px', borderBottom: '1px solid #f0f0f0', fontSize: 13,
 };
 const partCell = { minWidth: 0, overflow: 'hidden' };
 const partHeadStyle = { ...partRowStyle, padding: '6px 2px 4px', fontSize: 11, color: '#999' };
 const tagStyle = (kind) => ({
   fontSize: 11.5, fontWeight: 500, padding: '2px 7px', borderRadius: 3, whiteSpace: 'nowrap',
   background: kind === 'g' ? '#eaf6f0' : kind === 'r' ? '#fdeeed' : kind === 'a' ? '#fff7e6' : '#f2f3f5',
   color: kind === 'g' ? '#2e7d32' : kind === 'r' ? '#c62828' : kind === 'a' ? '#ad6800' : '#777',
 });

 // DAS-3337: the size rule (new money into a DeGiro account) and the year streak
 // (failing / having failed last year), both read from the same sleeve_passmark row
 // DAS-3334/DAS-3335 add new_money_eur and failed_years_before to.
 const sizeTag = (pm) => {
   const newMoney = pm && pm.new_money_eur;
   if (!newMoney || newMoney <= 0) return null;
   return {
     label: `€${Math.round(newMoney).toLocaleString('en-US')} new money in`,
     hover: 'This DeGiro account may only grow with its own gains (strategy, section 15) — new money has gone in since the size rule started.',
   };
 };
 const yearsTag = (pm) => {
   const failedBefore = pm && pm.failed_years_before;
   if (!failedBefore || failedBefore < 1) return null;
   if (pm.status === 'FAIL') {
     return { label: 'failing 2nd year', kind: 'r',
       hover: 'This account failed its pass mark last year too — two years in a row.' };
   }
   return { label: 'failed last year', kind: 'a',
     hover: 'This account passes its pass mark today, but failed it last year.' };
 };
 // DAS-3362: swing (volatility against its own benchmark) next to the pass mark —
 // W&O and any account without a risk band (pm.band null) gets no tag at all.
 const swingTag = (pm) => {
   const ratio = pm && pm.swing_ratio;
   const band = pm && pm.band;
   if (ratio == null || band == null) return null;
   const above = ratio > band;
   return {
     label: `swing ${ratio.toFixed(2)}× / band ${band.toFixed(2)}×`,
     kind: above ? 'r' : 'n',
     hover: above
       ? `This account's swing is running above its risk band (strategy, section 15).`
       : `This account's swing is within its risk band (strategy, section 15).`,
   };
 };

 const headerContent = (
   <div>
     YTD Result: <span className={headerStyle}>{resultText}</span>, YTD Performance: {performanceText}
   </div>
 );


 return (
  <>
    {/* DAS-2920 (Shape A): the answer first. One sentence, then which part pulled
        which way. Every block that used to be stacked below is still here — folded
        into the shelf at the bottom, in the same reading order DAS-2792 set. */}
    <div data-testid="portfolio-verdict" style={{ padding: '2px 0 0' }}>
      <div style={kickerStyle}>This year · all accounts</div>

      <div className={headerStyle} style={{ fontSize: 30, lineHeight: 1.15, marginTop: 6, fontWeight: 500 }}>
        {(overallResultEur == null || overallYtdPct == null) ? (
          <span style={{ color: '#888', fontSize: 20 }}>No portfolio figures yet — press Refresh.</span>
        ) : (
          <>
            {overallResultEur >= 0 ? 'Up ' : 'Down '}
            <span style={{ color: overallResultEur >= 0 ? '#2e7d32' : '#c62828' }}>
              {euro0(Math.abs(overallResultEur))}
            </span>
            {' — that’s '}
            <span style={{ color: overallYtdPct >= 0 ? '#2e7d32' : '#c62828' }}>
              {overallYtdPct.toFixed(2)}%
            </span>.
          </>
        )}
      </div>

      {overallDiff != null && (
        <div data-testid="portfolio-verdict-benchmark" style={{ marginTop: 8, color: '#555', maxWidth: '62ch' }}>
          A matching mix of index funds would have made {overallBenchPct.toFixed(2)}%. You are{' '}
          <strong style={{ color: overallDiff >= 0 ? '#2e7d32' : '#c62828' }}>
            {Math.abs(overallDiff).toFixed(2)} points {overallDiff >= 0 ? 'ahead of' : 'behind'}
          </strong>{' '}it.
          <InfoTooltip placement="top" text="The benchmark is built to match your own mix of shares, bonds and cash, in euros — so it is a fair comparison, not your euro portfolio against a dollar index. The full breakdown per account is in 'Fair comparison' below." />
        </div>
      )}

      {twrPerformance != null && (
        <div data-testid="status-twr-secondary" style={{ marginTop: 4, fontSize: 12, color: '#999' }}>
          Measured a second way (time-weighted): {twrPerformance.toFixed(2)}%
          <InfoTooltip placement="top" text="Two honest ways to measure the same year. The big number is return on the money you put in. Time-weighted ignores when you added or took out money — it is the one on the home chart. They differ when you moved money during the year." />
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 26px', marginTop: 14, paddingTop: 12, borderTop: '1px solid #eee', fontSize: 13, color: '#999' }}>
        {manualRecords.length > 0 && (
          <div>Everything you own <b className={headerStyle} style={{ color: '#333' }}>{euro0(totalAssetValue)}</b></div>
        )}
        <div>Tradeable <b className={headerStyle} style={{ color: '#333' }}>{euro0(tradeableValue)}</b></div>
        <div>Cash <b className={headerStyle} style={{ color: '#333' }}>{euro0(numOf(overallToplineData.cash_eur))}</b></div>
        <div>
          Last updated <b style={{ color: '#333' }}>{overallToplineData.epoch ? calculateHoursAgo(overallToplineData.epoch) : 'N/A'}</b>
          <Button type="link" size="small" onClick={() => handleUpdateClick('overall')} disabled={isAnyUpdateLoading}>
            {isUpdateLoading.overall ? <Spin size="small" /> : 'Update all'}
          </Button>
        </div>
      </div>
    </div>

    {rankedParts.length > 0 && (
      <>
      <div data-testid="portfolio-parts-ranked" style={{ marginTop: 22 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center' }}>
          <span style={seclineStyle}>Which part pulled which way</span>
          <InfoTooltip placement="top" text="Every account against its OWN fair benchmark — one matched to that account's mix of shares, bonds and cash. Worst first. Click a name to open that account's positions. Pass mark = the rule you set per account in your strategy, section 15." />
        </span>
        <div style={{ marginTop: 6, borderTop: '1px solid #f0f0f0' }}>
          <div data-testid="part-rows-header" style={partHeadStyle}>
            <span />
            <span style={{ textAlign: 'right' }}>Value</span>
            <span style={{ textAlign: 'right' }}>This year</span>
            <span style={{ textAlign: 'right' }}>Against its own benchmark</span>
            <span style={{ textAlign: 'center' }}>{sleevePassmark && sleevePassmark.length > 0 ? 'Pass mark' : ''}</span>
            <span style={{ textAlign: 'right' }}>Updated</span>
          </div>
          {rankedParts.map(pt => (
            <div key={pt.acctKey} data-testid={`part-row-${pt.acctKey}`} style={partRowStyle}>
              <span
                data-testid={`part-row-link-${pt.acctKey}`}
                onClick={() => handleAccountHeaderClick(pt.acctKey, pt.label, pt.topline)}
                style={{ ...partCell, cursor: 'pointer', color: '#1890ff', fontWeight: 600, whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}
                title={`Click to view ${pt.label} portfolio details`}
              >
                {pt.label}{preloadBadge(pt.acctKey)}
              </span>
              <span className={headerStyle} style={{ ...partCell, textAlign: 'right' }}>{euro0(pt.value)}</span>
              <span style={{ ...partCell, textAlign: 'right' }}>{pt.ytd != null ? fmtPctColored(pt.ytd) : <span style={{ color: '#bbb' }}>—</span>}</span>
              <span style={{ ...partCell, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 9 }}>
                {pt.diff != null && (
                  <i style={{ display: 'inline-block', height: 7, borderRadius: 1, flexShrink: 1,
                              width: Math.max(3, gapBarWidth(pt.diff)),
                              background: Math.abs(pt.diff) < 0.5 ? '#d9d9d9' : (pt.diff > 0 ? '#2e7d32' : '#c62828') }} />
                )}
                <span style={tagStyle(pt.diff == null ? 'n' : Math.abs(pt.diff) < 0.5 ? 'n' : pt.diff > 0 ? 'g' : 'r')}>
                  {pt.diff == null ? 'no fair benchmark'
                    : Math.abs(pt.diff) < 0.5 ? 'level with it'
                    : `${Math.abs(pt.diff).toFixed(2)} pts ${pt.diff > 0 ? 'ahead' : 'behind'}`}
                </span>
              </span>
              <span style={{ ...partCell, textAlign: 'center', display: 'flex', flexWrap: 'wrap', gap: 4, justifyContent: 'center' }}>
                {sleevePassmark && sleevePassmark.length > 0 && (() => {
                  // DAS-3319: PASS / FAIL only; the rule and today's figure are on hover.
                  const pm = passmarkByAccount[pt.benchKey];
                  const chip = passmarkChip(pm);
                  const size = sizeTag(pm);
                  const years = yearsTag(pm);
                  const swing = swingTag(pm);
                  return (
                    <>
                      <span data-testid="sleeve-passmark-tag" title={chip.hover}
                            data-window={(pm && pm.window) || undefined}
                            style={{ ...tagStyle(chip.kind), cursor: 'help' }}>{chip.label}</span>
                      {swing && (
                        <span data-testid="sleeve-swing-tag" title={swing.hover}
                              style={{ ...tagStyle(swing.kind), cursor: 'help' }}>{swing.label}</span>
                      )}
                      {size && (
                        <span data-testid="sleeve-size-tag" title={size.hover}
                              style={{ ...tagStyle('a'), cursor: 'help' }}>{size.label}</span>
                      )}
                      {years && (
                        <span data-testid="sleeve-years-tag" title={years.hover}
                              style={{ ...tagStyle(years.kind), cursor: 'help' }}>{years.label}</span>
                      )}
                    </>
                  );
                })()}
              </span>
              {/* DAS-3319: on a narrow window the button drops under the time instead of being cut off */}
              <span style={{ ...partCell, textAlign: 'right', color: '#aaa', fontSize: 12 }}>
                <span style={{ whiteSpace: 'nowrap' }}>{pt.epoch ? calculateHoursAgo(pt.epoch) : 'N/A'}</span>
                {(pt.updateKey === 'saxo' && saxoNeedsReconnect) ? (
                  <Button type="link" size="small" danger data-testid="saxo-reconnect-button"
                          onClick={() => setReconnectOpen(true)}>
                    Reconnect
                  </Button>
                ) : (
                  <Button type="link" size="small" onClick={() => handleUpdateClick(pt.updateKey)} disabled={isAnyUpdateLoading}>
                    {isUpdateLoading[pt.updateKey] ? <Spin size="small" /> : 'Update'}
                  </Button>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <SleeveGapChart labels={{
          ...rankedParts.reduce((acc, p) => ({ ...acc, [p.benchKey]: p.label }), {}),
          overall: 'Overall',
        }} />
        <SleeveHoldingEffects labels={rankedParts.reduce((acc, p) => ({ ...acc, [p.benchKey]: p.label }), {})} />
      </div>
      </>
    )}

    <div style={{ marginTop: 22 }} data-testid="portfolio-detail-shelf">
      <div style={seclineStyle}>The detail, folded away</div>
      <Collapse style={{ marginTop: 6 }}>
        <Panel forceRender header="All the numbers, account by account" key="numbers">
          <Table dataSource={dataSource} columns={baseColumns} pagination={false} />
        </Panel>

        <Panel forceRender header="Cash income" key="byPart">

        {/* DAS-2558 / DAS-2792: per-part return matrix — price (YTD from broker
            toplines; 1D/1W/1M TWRR) + income + total. The DAS-2792 reading order is
            preserved as the order of the folded rows. */}
        {partReturns && Object.keys(partReturns).length ? (() => {
          const PART_ORDER = [['overall', 'Overall'], ['saxo', 'Saxo'], ['degiro', 'DeGiro'], ['degiro_rebalance', 'DeGiro Rebal.'], ['WenO', 'W&O'], ['manual', 'Other']];
          const PERIODS = [['one_day', 'Today'], ['one_week', '1W'], ['one_month', '1M'], ['ytd', 'YTD']];
          const num = (x) => { const v = parseFloat(x); return isNaN(v) ? null : v; };
          const ytdPrice = {
            overall: num(overallToplineData.portfolio_performance),
            saxo: num(saxoToplineData.Saxo_portfolio_performance),
            degiro: num(dgToplineData.portfolio_performance),
            degiro_rebalance: num(dgRebalanceToplineData && dgRebalanceToplineData.portfolio_performance),
            WenO: num(otherData.performance),
            manual: null,
          };
          const parts = PART_ORDER.filter(([k]) => partReturns[k]);
          const cell = { padding: '2px 8px', textAlign: 'right', whiteSpace: 'nowrap' };
          const bl = { borderLeft: '1px solid #eee' };
          return (
            <div style={{ margin: '0 0 12px', fontSize: 12, overflowX: 'auto' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13 }}>
                <strong>Return by part</strong>
                <InfoTooltip placement="top" text="Per portfolio part and period. Price: YTD uses the broker's own figure (the same one shown in the table below); 1D/1W/1M are cashflow-adjusted time-weighted returns from the broker value series. Income: realized dividends/coupons/interest over the period (€ shown; % of part value on hover). Total = price + income. '—' = no data for that part/period (W&O savings & real estate have no return series; income only where captured)." />
              </span>
              <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
                <thead>
                  <tr style={{ color: '#888' }}>
                    <th style={{ textAlign: 'left', padding: '2px 10px 2px 0' }}></th>
                    {PERIODS.map(([pk, pl]) => (
                      <th key={pk} colSpan={3} style={{ textAlign: 'center', padding: '2px 8px', ...bl }}>{pl}</th>
                    ))}
                  </tr>
                  <tr style={{ color: '#aaa', fontWeight: 400 }}>
                    <th style={{ textAlign: 'left', padding: '1px 10px 4px 0' }}>Part</th>
                    {PERIODS.map(([pk]) => (
                      <React.Fragment key={pk}>
                        <th style={{ ...cell, ...bl }}>Price</th>
                        <th style={cell}>Inc €</th>
                        <th style={cell}>Total</th>
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {parts.map(([pk, pl]) => {
                    const sp = partReturns[pk].short_price || {};
                    const inc = partReturns[pk].income || {};
                    return (
                      <tr key={pk} style={{ fontWeight: pk === 'overall' ? 600 : 400, borderTop: pk === 'overall' ? 'none' : '1px solid #f5f5f5' }}>
                        <td style={{ padding: '2px 10px 2px 0', textAlign: 'left' }}>{pl}</td>
                        {PERIODS.map(([perk, perl]) => {
                          const price = perk === 'ytd' ? ytdPrice[pk] : (sp[perk] == null ? null : sp[perk]);
                          const i = inc[perk] || {};
                          const iEur = i.eur ? i.eur : 0;
                          const iPct = i.pct != null ? i.pct : 0;
                          const tot = price != null ? price + iPct : null;
                          // DAS-2621: Inc € cell drills down into the underlying dividend payouts.
                          const incClickable = iEur !== 0;
                          return (
                            <React.Fragment key={perk}>
                              <td style={{ ...cell, ...bl }}>{price != null ? fmtPctColored(price) : <span style={{ color: '#bbb' }}>—</span>}</td>
                              <td style={{ ...cell, color: iEur > 0 ? '#2e7d32' : iEur < 0 ? '#c62828' : '#bbb',
                                           cursor: incClickable ? 'pointer' : 'default',
                                           textDecoration: incClickable ? 'underline' : 'none' }}
                                  title={incClickable
                                    ? `Click for the ${perl} payout details${i.pct != null ? ` (${i.pct}% of part value)` : ''}`
                                    : (pk === 'WenO'
                                        ? "W&O funds reinvest all income inside the fund — it's already included in the Price/Total return, not paid out as cash."
                                        : (i.pct != null ? `${i.pct}% of part value` : ''))}
                                  onClick={incClickable ? () => openIncomeModal(pk, pl, perk, perl) : undefined}>
                                {iEur
                                  ? `€${Math.round(iEur).toLocaleString()}`
                                  : (pk === 'WenO'
                                      ? <span style={{ color: '#bbb', fontStyle: 'italic' }}>reinv.</span>
                                      : '—')}
                              </td>
                              <td style={cell}>{tot != null ? fmtPctColored(tot) : <span style={{ color: '#bbb' }}>—</span>}</td>
                            </React.Fragment>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })() : null}
        {/* DAS-2740 / DAS-2920: the YTD income split sits in the SAME section as the
            return matrix — it is the same income, totalled by kind rather than by part. */}
        {incomeCategoryTotals && (() => {
          const eur = (x) => { const v = parseFloat(x); return isNaN(v) ? 0 : v; };
          const fmtEur = (v) => v ? `€${Math.round(v).toLocaleString()}` : <span style={{ color: '#bbb' }}>—</span>;
          const rows = [
            ['equity_dividend', 'Equity dividend', 'Cash dividends from shares and share funds.'],
            ['bond_coupon', 'Bond coupon', 'Coupons / rente from bonds and bond funds (accrued interest netted).'],
            ['interest', 'Interest', 'Interest earned on idle cash.'],
          ];
          const cellR = { padding: '2px 10px', textAlign: 'right', whiteSpace: 'nowrap' };
          const reinv = eur(incomeCategoryTotals.reinvested);
          return (
            <div data-testid="income-category-totals-ytd" style={{ margin: '0 0 14px', fontSize: 12, overflowX: 'auto' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13 }}>
                <strong>Totals YTD per category</strong>
                <InfoTooltip placement="top" text="Your year-to-date income across the whole portfolio, split by kind — the cash actually received. 'Of which reinvested' is a memo: the part of the income above that was automatically put back into more units instead of paid out as cash. It is already counted in the categories above, not added on top. Income that funds accumulate internally (e.g. W&O) is not a separate cash amount and appears in the Price/Total return instead." />
              </span>
              <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
                <tbody>
                  {rows.map(([k, label, tip]) => {
                    const v = eur(incomeCategoryTotals[k]);
                    return (
                      <tr key={k} style={{ borderTop: '1px solid #f5f5f5' }}>
                        <td style={{ padding: '2px 10px 2px 0', textAlign: 'left' }} title={tip}>{label}</td>
                        <td style={{ ...cellR, color: v > 0 ? '#2e7d32' : v < 0 ? '#c62828' : '#bbb' }}>{fmtEur(v)}</td>
                      </tr>
                    );
                  })}
                  <tr style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '2px 10px 2px 16px', textAlign: 'left', color: '#888', fontStyle: 'italic' }}
                        title="Of the income above, the part that was automatically reinvested (bought more units) rather than paid out as cash. Already included in the categories above — shown for insight, not added on top.">
                      of which reinvested
                    </td>
                    <td style={{ ...cellR, color: '#888', fontStyle: 'italic' }}>
                      {reinv ? `€${Math.round(reinv).toLocaleString()}` : <span style={{ color: '#bbb' }}>—</span>}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          );
        })()}
        </Panel>

        <Panel forceRender header="Fair comparison — every part against a matched index mix" key="fair">
        {/* DAS-2726 (slice 4): risk-matched "fair comparison" — each sleeve's YTD vs a
            benchmark matched to its own asset mix (equity/bond/cash), all in euros. */}
        {sleeveBenchmark && sleeveBenchmark.length ? (() => {
          const num = (x) => { const v = parseFloat(x); return isNaN(v) ? null : v; };
          const yourYtd = {
            overall: num(overallToplineData.portfolio_performance),
            saxo: num(saxoToplineData.Saxo_portfolio_performance),
            degiro: num(dgToplineData.portfolio_performance),
            degiro_rebalance: num(dgRebalanceToplineData && dgRebalanceToplineData.portfolio_performance),
            WenO: num(otherData.performance),
          };
          const LABELS = { overall: 'Overall (tradeable)', saxo: 'Saxo', degiro: 'DeGiro', degiro_rebalance: 'DeGiro Rebal.', WenO: 'W&O' };
          const bmk = {}; sleeveBenchmark.forEach(b => { bmk[b.account] = b; });
          const risk = {}; sleeveRisk.forEach(r => { risk[r.account] = r; });
          const rows = Object.keys(LABELS).filter(k => bmk[k]);
          if (!rows.length) return null;
          // Your sleeve's realized volatility, and (for Overall) a value-weighted blend
          // of the sleeve vols so the Overall row's "your risk" isn't blank.
          const sleeveVal = {
            saxo: num(saxoToplineData.Saxo_portfolio_total_value),
            degiro: num(dgToplineData.portfolio_total_value),
            degiro_rebalance: num(dgRebalanceToplineData && dgRebalanceToplineData.portfolio_total_value),
            WenO: num(otherData.value),
          };
          const yoursVol = {};
          ['saxo', 'degiro', 'degiro_rebalance', 'WenO'].forEach(k => {
            const v = risk[k] ? num(risk[k].annualized_volatility) : null;
            if (v != null) yoursVol[k] = v;
          });
          // DAS-2739: the Overall "your risk" is a size-weighted blend of ALL FOUR sleeves'
          // volatilities (each weighted by its value). This is the one number that covers 100%
          // of the portfolio — including the calm W&O third (~€237k), whose low ~5% pulls the
          // blend down. It does NOT fully account for sleeves not moving in lockstep, so it's an
          // approximation (labelled as such in the hover). Owner decision (DAS-2739): prefer full
          // coverage over a "true-combined" figure that would have to drop W&O (no daily prices).
          let wSum = 0, vSum = 0;
          ['saxo', 'degiro', 'degiro_rebalance', 'WenO'].forEach(k => {
            if (yoursVol[k] != null && sleeveVal[k] != null && sleeveVal[k] > 0) {
              wSum += sleeveVal[k] * yoursVol[k]; vSum += sleeveVal[k];
            }
          });
          if (vSum > 0) yoursVol.overall = wSum / vSum;
          // DAS-2752: total tradeable value for the euro-swing translation.
          const vSumAll = Object.values(sleeveVal).reduce((s, v) => s + (v > 0 ? v : 0), 0);
          // DAS-2726: per-row explanation of the blended benchmark — names the
          // components and why they're weighted this way (matches the sleeve's mix).
          const bmkTitle = (r) => {
            const eq = Math.round(num(r.equity_pct) || 0);
            const bd = Math.round(num(r.bond_pct) || 0);
            const ca = Math.round(num(r.cash_pct) || 0);
            const parts = [];
            if (eq > 0) parts.push(`${eq}% MSCI World (global equities, converted to €)`);
            if (bd > 0) parts.push(`${bd}% € Govt Bonds 1-3y (${r.bond_ticker || 'IBGS.AS'})`);
            if (ca > 0) parts.push(`${ca}% ECB cash rate`);
            return `Blended benchmark, matched to this sleeve's asset mix: ${parts.join(' + ')}. `
              + `Chosen so a ${eq}% equity / ${bd}% bond sleeve is judged against a same-mix, `
              + `euro-denominated benchmark — not against 100% equities. `
              + `The bond part is euro government 1-3 year because your bonds are short-dated `
              + `euro paper and floating-rate funds.`;
          };
          // DAS-2735: compact per-row benchmark name/spec for the visible "Benchmark" column
          // (the full sentence stays on the 'Matched bmk' hover via bmkTitle).
          const bmkLabel = (r) => {
            const eq = Math.round(num(r.equity_pct) || 0);
            const bd = Math.round(num(r.bond_pct) || 0);
            const ca = Math.round(num(r.cash_pct) || 0);
            const parts = [];
            if (eq > 0) parts.push(`${eq}% MSCI World€`);
            if (bd > 0) parts.push(`${bd}% ${r.bond_ticker || 'IBGS.AS'}`);
            if (ca > 0) parts.push(`${ca}% ECB cash`);
            return parts.join(' + ') || '—';
          };
          const cell = { padding: '2px 12px', textAlign: 'right', whiteSpace: 'nowrap' };
          return (
            <div data-testid="sleeve-fair-comparison" style={{ margin: '16px 0 4px', fontSize: 12, overflowX: 'auto' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13 }}>
                <strong>Fair comparison (risk-matched, YTD)</strong>
                <InfoTooltip placement="top" text="Each sleeve's YTD return vs a benchmark matched to ITS asset mix (equity/bond/cash) — a fair, like-for-like comparison. Everything is in euros: the equity benchmark is MSCI World CONVERTED from dollars to euros (otherwise a strong-dollar year makes the US market look artificially low next to your euro return). Each sleeve's benchmark is a mix-weighted blend of MSCI World (equity, in €), € Government Bonds 1-3y (IBGS.AS) and ECB cash — hover a 'Matched bmk' value to see that sleeve's exact blend. Why these three: shares = MSCI World in euros, because you pick from the whole developed world and live in euros. Bonds = euro government 1-3 year, because your bonds are short-dated euro paper and floating-rate funds. Cash = the ECB deposit rate, because that is what idle euros earn. Diff = your return minus the matched benchmark (green = beating a fair benchmark). Risk (you/bmk) = roughly how much your money swings in a typical year — yours vs a fair same-mix benchmark. Bigger = bumpier; red = you swing more than the benchmark. Measured from the past year of day-to-day moves. The Overall figure blends all your parts by size (it includes W&O), so treat it as an approximation. Hover any Risk cell to see what it covers." />
              </span>
              <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
                <thead>
                  <tr style={{ color: '#aaa' }}>
                    <th style={{ textAlign: 'left', padding: '2px 12px 2px 0' }}>Part</th>
                    <th style={cell}>Your YTD</th>
                    <th style={cell}>Matched bmk</th>
                    <th style={{ ...cell, textAlign: 'left' }} data-testid="fair-benchmark-col" title="The specific benchmark blend matched to this sleeve's asset mix.">Benchmark</th>
                    <th style={cell}>Diff</th>
                    <th style={cell}>Mix eq/bd</th>
                    <th style={cell} title="Roughly how much each part's value swings in a typical year (bigger = bumpier) — yours vs a matching benchmark's. Red = you swing more than a fair benchmark.">Risk you/bmk</th>
                    <th style={cell} title="The long view: your return over this part's WHOLE history vs the same-mix benchmark over the same window. YTD answers 'is something broken?'; only multiple years answer 'is this part good?'. Hover a value for the window and caveats.">Since start</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(k => {
                    const y = yourYtd[k];
                    const b = num(bmk[k].bench_ytd);
                    const diff = (y != null && b != null) ? y - b : null;
                    const yVol = yoursVol[k] != null ? yoursVol[k] : null;
                    const bVol = num(bmk[k].bench_vol);
                    const eq = num(bmk[k].equity_pct), bd = num(bmk[k].bond_pct);
                    // DAS-2739: plain-language, per-record explanation of each Risk number.
                    const riskTitle = (yVol != null && bVol != null)
                      ? `Your ${yVol.toFixed(1)}% = roughly how much ${
                          k === 'overall' ? 'your whole portfolio' : LABELS[k]
                        }'s value swings in a typical year — ${
                          k === 'WenO' ? 'based on its monthly ups and downs'
                          : k === 'overall' ? "a size-weighted blend of all your parts (an approximation — it doesn't fully account for your parts not moving together)"
                          : 'based on its day-to-day moves over the past year'
                        }. Benchmark ${bVol.toFixed(1)}% = the same for a matching mix of world shares + € bonds. Bigger = bumpier; you swung ${yVol > bVol ? 'MORE' : 'less'} than the fair benchmark.${(() => {
                          // DAS-2752: translate the vol % into euros on this part's value.
                          const pv = k === 'overall' ? vSumAll : sleeveVal[k];
                          return (yVol != null && pv > 0)
                            ? ` In euros: about €${Math.round(yVol * pv / 100).toLocaleString('nl-NL')} of typical yearly movement on €${Math.round(pv).toLocaleString('nl-NL')}.`
                            : '';
                        })()}`
                      : '';
                    // DAS-2752: euro reconciliation hover for "Your YTD" — only for parts the
                    // backend deemed reliable (gate). Traces the % to checkable euros.
                    const rec = partRecon[k];
                    const reconTitle = rec
                      ? `In euros: started ${rec.start_date} at ${formatEuro(rec.start_value)}, now ${formatEuro(rec.current_value)}${
                          rec.net_flows != null && Math.abs(rec.net_flows) > 0.01
                            ? `, of which ${formatEuro(rec.net_flows)} was deposits/withdrawals` : ''
                        } → ${formatEuro(rec.eur_earned)} earned${
                          rec.start_value > 0 ? ` (${((rec.eur_earned / rec.start_value) * 100).toFixed(1)}% on the start value)` : ''
                        }.${rec.net_flows == null ? ' Deposits/withdrawals not tracked for this part — euros shown are the raw value change.' : ''}`
                      : '';
                    // DAS-2751: same-method measurement next to the broker figure, with a
                    // divergence flag when they disagree by more than half a point.
                    const uni = partUniform[k] && partUniform[k].ytd != null ? Number(partUniform[k].ytd) : null;
                    const uniformLine = uni != null
                      ? `Measured our own way (one identical method for every part): ${uni.toFixed(2)}% YTD.${
                          (y != null && Math.abs(uni - y) > 0.5)
                            ? ` ⚠ differs from the broker's ${Number(y).toFixed(2)}% — the two measure differently; if this gap persists it deserves a look.`
                            : ''
                        }`
                      : '';
                    const ytdTitle = [reconTitle, uniformLine].filter(Boolean).join(' ');
                    return (
                      <tr key={k} style={{ borderTop: k === 'overall' ? '2px solid #ddd' : '1px solid #f5f5f5', fontWeight: k === 'overall' ? 600 : 400 }}>
                        <td style={{ padding: '2px 12px 2px 0', textAlign: 'left' }}>{LABELS[k]}</td>
                        <td style={ytdTitle ? { ...cell, cursor: 'help', textDecoration: 'underline dotted' } : cell} title={ytdTitle}>{y != null ? fmtPctColored(y) : <span style={{ color: '#bbb' }}>—</span>}</td>
                        <td style={{ ...cell, cursor: 'help', textDecoration: 'underline dotted' }} title={bmkTitle(bmk[k])}>{b != null ? `${b.toFixed(2)}%` : '—'}</td>
                        <td style={{ ...cell, textAlign: 'left', color: '#888' }}>{bmkLabel(bmk[k])}</td>
                        <td style={cell}>{diff != null ? fmtPctColored(diff) : <span style={{ color: '#bbb' }}>—</span>}</td>
                        <td style={cell}>{eq != null ? `${Math.round(eq)}/${Math.round(bd || 0)}` : '—'}</td>
                        <td style={{ ...cell, cursor: 'help' }} title={riskTitle}>
                          <span style={{ color: (yVol != null && bVol != null && yVol > bVol * 1.1) ? '#c62828' : '#333' }}>{yVol != null ? `${yVol.toFixed(1)}%` : '—'}</span>
                          <span style={{ color: '#aaa' }}>{' / '}{bVol != null ? `${bVol.toFixed(1)}%` : '—'}</span>
                        </td>
                        {(() => {
                          // DAS-2750: the long view — your full-history return vs the
                          // same-window matched benchmark (constant current mix).
                          const ssY = partUniform[k] && partUniform[k].since_start != null ? Number(partUniform[k].since_start) : null;
                          const ssB = num(bmk[k].bench_since_start);
                          const ssDate = (partUniform[k] && partUniform[k].since_date) || bmk[k].since_start_date || null;
                          const ssTitle = (ssY != null || ssB != null)
                            ? `The long view, since ${ssDate || 'this part’s data start'}: you ${ssY != null ? ssY.toFixed(1) + '%' : '—'} vs benchmark ${ssB != null ? ssB.toFixed(1) + '%' : '—'} over the same window. Benchmark uses today's mix for the whole period (an approximation) and includes dividends. Judge skill here, not on YTD.`
                            : '';
                          return (
                            <td style={ssTitle ? { ...cell, cursor: 'help', textDecoration: 'underline dotted' } : cell} title={ssTitle} data-testid="fair-since-start-col">
                              <span style={{ color: (ssY != null && ssB != null) ? (ssY >= ssB ? '#2e7d32' : '#c62828') : '#333' }}>{ssY != null ? `${ssY.toFixed(1)}%` : '—'}</span>
                              <span style={{ color: '#aaa' }}>{' / '}{ssB != null ? `${ssB.toFixed(1)}%` : '—'}</span>
                            </td>
                          );
                        })()}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })() : null}
        {/* DAS-2824: the owner's own crash behaviour — every reviewed storm and what the
            trades on panic days cost or earned versus doing nothing (Panic_Review_Log,
            measured 3 months after the storm). History, not an alert: nothing to act on,
            everything to learn from. */}
        {panicReview && panicReview.length ? (
          <div data-testid="das2824-panic-review" style={{ margin: '12px 0 4px', fontSize: 12 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', fontSize: 13 }}>
              <strong>Crash behaviour — what your trades on panic days did</strong>
              <InfoTooltip placement="top" text="For every market storm (PANIC days in the Market Weather), the trades you made on those days are re-priced three months later against doing nothing. Negative = the panic trades cost you money (usually selling into the fall); positive = they earned (usually buying into it). Computed once per storm, 90 days after it ends, by the daily post-panic review. This is history to learn from, not an alert to act on." />
            </span>
            <table style={{ borderCollapse: 'collapse', marginTop: 4 }}>
              <thead>
                <tr style={{ color: '#aaa' }}>
                  <th style={{ textAlign: 'left', padding: '2px 12px 2px 0' }}>Storm</th>
                  <th style={{ padding: '2px 12px', textAlign: 'right' }}>Trades on panic days</th>
                  <th style={{ padding: '2px 12px', textAlign: 'right' }}>Net effect vs doing nothing</th>
                </tr>
              </thead>
              <tbody>
                {panicReview.map(r => {
                  const net = parseFloat(r.net_effect_eur);
                  return (
                    <tr key={`${r.episode_start}-${r.episode_end}`} title={r.report_text || ''}>
                      <td style={{ padding: '2px 12px 2px 0' }}>{r.episode_start} → {r.episode_end}</td>
                      <td style={{ padding: '2px 12px', textAlign: 'right' }}>{r.trades_in_episode}</td>
                      <td style={{ padding: '2px 12px', textAlign: 'right', fontWeight: 600, color: net < 0 ? '#cf1322' : '#2c7a44' }}>
                        {net < 0 ? 'cost ' : 'earned '}{formatEuro(Math.abs(net))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        </Panel>

        {/* DAS-2994 (was DAS-2826): every sale from both brokers, judged against the market
            and the owner's own sleeve from the sale day. Read from Judged_Sales (nightly). */}
        <Panel forceRender
               header={<span data-testid="closed-trades-button">Completed trades — was each sale, on that day, the right call?</span>}
               key="closed">
          <ClosedTradesPanel userId={userId} />
        </Panel>
        <Panel forceRender header="What it cost you" key="costs">
          {/* DAS-2777: costs per component, by type */}
          <CostsSection />
        </Panel>

        <Panel forceRender header="Scoreboard — did your own trades help?" key="scoreboard">
          {/* DAS-2766: DeGiro-only history */}
          <ScoreboardPanel />
        </Panel>

        <Panel forceRender header="The rebuy tax — what selling and buying back cost you" key="rebuy">
          {/* DAS-2770: DeGiro-only history */}
          <RebuyHistoryPanel />
        </Panel>
      </Collapse>
    </div>

    {/* DAS-2920: the Saxo reconnect used to sit in the page flow between the rebuy
        tax and the benchmark sections — machine plumbing in the middle of the money,
        with a 2FA code box permanently on screen. It now opens from the Saxo row. */}
    <Modal
      open={reconnectOpen}
      title="Reconnect Saxo"
      onCancel={() => setReconnectOpen(false)}
      footer={null}
      width={480}
    >
      <div data-testid="saxo-reconnect-modal">
        <p style={{ color: '#555', marginTop: 0 }}>
          Saxo has stopped accepting the saved login, so prices and positions are no longer
          coming in. Press Reconnect — Saxo sends a code by text message, and you type it below.
        </p>
        <Button
          type="primary"
          onClick={handleRefreshToken}
          disabled={is2FAStep || isLoading}
        >
          {isLoading ? <Spin /> : 'Refresh token'}
        </Button>

        {is2FAStep && (
          <div style={{ marginTop: '20px' }}>
            <Input
              type="text"
              value={smsCode}
              onChange={(e) => setSmsCode(e.target.value)}
              placeholder="Enter the 2FA code"
              style={{ width: '260px', marginRight: '10px' }}
            />
            <Button
              type="primary"
              onClick={handleSmsSubmit}
              disabled={isSmsLoading}
            >
              {isSmsLoading ? <Spin /> : 'Submit'}
            </Button>
          </div>
        )}
      </div>
    </Modal>

    {/* DAS-1939: Account Portfolio Modal */}
    <AccountPortfolioModal
      visible={accountModalVisible}
      onClose={() => setAccountModalVisible(false)}
      account={selectedAccount}
      accountDisplayName={selectedAccountDisplayName}
      toplineData={selectedAccountTopline}
      alerts={portfolio?.data?.pf_alert || []}
    />

    {/* DAS-2621: Dividend payout drill-down for the Return-by-part matrix */}
    <IncomeDetailsModal
      open={incomeModal.open}
      onClose={closeIncomeModal}
      part={incomeModal.part}
      partLabel={incomeModal.partLabel}
      period={incomeModal.period}
      periodLabel={incomeModal.periodLabel}
    />
  </>
);

};

export default ToplineTable;
